import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Bid, BID_STATUS_STRING, CarrierNotes } from 'cargo-chief-commons';
import { takeUntil } from 'rxjs/operators';

import { BaseComponent } from '@shared/base/base.component';
import { LogCallService } from '@service/log-call/log-call.service';
import { BidsFacade } from '../../v2/bids/services/bids.facade';

// CN-8940: declared in SharedModule (not BidsModule) so the shared tabbed table can
// render it inside the Offers carrier-name cell. Both of its dependencies are
// providedIn: 'root', so nothing about the move ties SharedModule to BidsModule.
@Component({
	selector: 'app-bid-contact-actions',
	templateUrl: './bid-contact-actions.component.html',
	styleUrls: ['./bid-contact-actions.component.scss'],
})
export class BidContactActionsComponent extends BaseComponent {
	@Input() bid: Bid;
	@Input() loadId: string;
	@Input() quoteLane: string;
	// CN-8940: compact mode for inline use inside a data cell (Offers carrier name)
	// rather than a dedicated column. Shrinks the hit area and icon so the row height
	// is unchanged, and reserves an empty slot for each missing action so the icons
	// line up vertically down the table instead of shifting per row.
	@Input() compact = false;

	@Output() bidContacted = new EventEmitter<Bid>();
	// CN-8646: the note created by the logged call, so the parent can insert it into
	// the Notes list immediately (the note write lags the list query).
	@Output() callLogged = new EventEmitter<CarrierNotes>();

	constructor(
		private readonly logCallService: LogCallService,
		private readonly bidsFacade: BidsFacade
	) {
		super();
	}

	// Prefer the resolved contact phone, fall back to the carrier contact phone.
	get phone(): string {
		return this.bid?.contactPhoneNumber || this.bid?.carrierContactPhone || null;
	}

	get email(): string {
		return this.bid?.contactEmail || null;
	}

	get contactName(): string {
		return this.bid?.contactFirstName || '';
	}

	// CN-8619: contact name + number/email surface on hover instead of as row text.
	get phoneTooltip(): string {
		if (!this.phone) {
			return '';
		}
		return this.contactName ? `${this.contactName} · ${this.phone}` : `Call ${this.phone}`;
	}

	get emailTooltip(): string {
		if (!this.email) {
			return '';
		}
		return this.contactName ? `${this.contactName} · ${this.email}` : `Email ${this.email}`;
	}

	// CN-8619: phone / email / Log Call all open the shared "Log a call" modal
	// (the same one Procurement Projects and the carrier-outreach widget use). The
	// phone/email anchors also fire tel:/mailto:. On a logged call, flip the row to
	// Contacted and bubble up.
	openLogCallDialog(): void {
		this.logCallService
			.openLogCall({
				carrierId: this.bid?.carrierId,
				loadId: this.loadId,
				bidId: this.bid?.id,
			})
			.pipe(takeUntil(this.isDestroyed$))
			.subscribe((result) => {
				this.bidsFacade.updateBid(
					this.bid.id,
					{ status: BID_STATUS_STRING.CONTACTED },
					this.quoteLane
				);
				// Emit a patched copy so listeners get the new Contacted status rather
				// than the stale bid object.
				this.bidContacted.emit({ ...this.bid, status: BID_STATUS_STRING.CONTACTED });
				// CN-8646: surface the created note so the Notes list shows it immediately.
				if (result?.note) {
					this.callLogged.emit(result.note);
				}
			});
	}
}
